import React from 'react';
import { twMerge } from 'tailwind-merge';
import { applyTypography, BaseButton } from '@repo/common';

import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

const baseClasses =
  'relative flex h-[38px] min-w-fit w-full items-center rounded-[8px] text-left transition-[background-color,opacity,color] duration-200 ease-out hover:bg-grayscale-gray01 active:bg-grayscale-gray01';

const containerClassesByState = (state, reserveCheckSlot) =>
  twMerge(
    baseClasses,
    reserveCheckSlot ? 'gap-[2px] pl-2 pr-3 py-[10px]' : 'px-3 py-[10px]',
    state === 'hover' ? 'bg-grayscale-gray01' : ''
  );

const checkSlotClasses =
  'flex h-[18px] w-4 shrink-0 items-center justify-center';

/**
 * @example
 * <PathfinderSingleSelectOption
 *   label="Immediate start available"
 *   state="default"
 *   onClick={handleSelect}
 * />
 *
 * @example
 * <PathfinderSingleSelectOption
 *   label="Flexible"
 *   state="selected"
 * />
 */

/**
 * Single-select option row for Pathfinder dropdowns.
 *
 * @component
 * @param {string} label Option text
 * @param {"default" | "hover" | "selected"} state Visual state from Figma
 * @param {boolean} reserveCheckSlot Keeps the leading 16px slot for selected lists
 * @param {string} className Extra Tailwind classes for layout overrides
 * @param {("button" | "submit" | "reset")} type Native button type
 *
 * Reuse notes
 * - Use inside a one-choice dropdown list
 * - Keep the parent dropdown width flexible; this row is `min-w-fit` so longer labels can define the menu width
 * - Use `state="selected"` only for the currently chosen item
 */
function PathfinderSingleSelectOption({
  label = 'option',
  state = 'default',
  reserveCheckSlot = true,
  className = '',
  type = 'button',
  ...props
}) {
  const isSelected = state === 'selected';

  return (
    <BaseButton
      type='text'
      size='m'
      htmlType={type}
      typo={false}
      isStatic
      leftalign
      gap={reserveCheckSlot ? 'gap-[2px]' : 'gap-0'}
      icon={
        reserveCheckSlot ? (
          <span className={checkSlotClasses} aria-hidden>
            {isSelected ? (
              <CheckIcon className='transition-opacity duration-200 ease-out opacity-100 size-4 text-text-primary' />
            ) : null}
          </span>
        ) : null
      }
      role='option'
      aria-selected={isSelected}
      className={twMerge(
        'min-w-0 cursor-pointer border-0 bg-transparent',
        containerClassesByState(state, reserveCheckSlot),
        className
      )}
      textWrapperClass={twMerge(
        'h-auto',
        applyTypography('b14', label),
        'whitespace-nowrap text-text-primary transition-colors duration-200 ease-out'
      )}
      {...props}
    >
      {label}
    </BaseButton>
  );
}

export default PathfinderSingleSelectOption;
